import { useState, type FormEvent } from 'react';
import { HTTPError } from 'ky';
import {
  useQuery, useQueryClient, useInfiniteQuery,
} from '@tanstack/react-query';
import {
  ArrowLeft, Users, UserPlus, Check, X, Loader2, Trophy, Zap, Flame,
  Award, CalendarRange, Target, Medal, Sparkles, Snowflake, Activity, Share2, Copy,
} from 'lucide-react';
import {
  fetchFriends, requestFriend, acceptFriend, declineFriend,
  removeFriend, fetchFeed, fetchReferral,
  type FriendCard, type FeedEventCard,
} from '@/lib/api';
import { toast } from '@/store/toast';
import { FriendsLeaderboardTabs } from '@/pages/leaderboard/FriendsLeaderboardPage';
import { useTranslation } from 'react-i18next';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './FriendsPage.module.css';

/** Translate fn for the 'friends' namespace, aliased so helper
 *  functions outside components can accept `t` without another import. */
type FriendsT = ReturnType<typeof useTranslation>['t'];

interface Props { onNavigate: (path: string) => void }

type Tab = 'friends' | 'requests' | 'find' | 'activity' | 'leaderboard';

const VALID_TABS = new Set<Tab>(['friends', 'requests', 'find', 'activity', 'leaderboard']);

/**
 * Read `?tab=` from the current URL once on mount. Defaults to
 * 'find' when absent or unrecognized — the page leads with
 * discovery. Deep links keep working: the
 * friend_request_received notification's `link` field
 * (`/friends?tab=requests`) still lands on Requests.
 *
 * One-shot read: subsequent in-page tab changes don't sync back
 * to the URL. Keeping the URL stable across tab switches is
 * the right tradeoff here — a deep link is intent at
 * navigation time, not a persistent state slot.
 */
function initialTabFromUrl(): Tab {
  if (typeof window === 'undefined') return 'find';
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('tab');
  if (raw && VALID_TABS.has(raw as Tab)) return raw as Tab;
  return 'find';
}

/**
 * Driver friends hub — three tabs.
 *
 * - **Friends**: accepted, sorted by recency. Each card shows the
 *   other driver's avatar, rank, weekly kWh, current streak.
 * - **Requests**: incoming (accept/decline) on top, outgoing
 *   (waiting) below.
 * - **Find**: text search + referral-code direct add. The same
 *   endpoint backs both — code-exact + username/name substring.
 *
 * No optimistic mutations — friends are low-frequency, the
 * round-trip is fast enough that "fire then refetch" feels right.
 */
export function FriendsPage({ onNavigate }: Props) {
  const { t } = useTranslation('friends');
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>(() => initialTabFromUrl());

  const friendsQ = useQuery({
    queryKey: ['friends', 'list'],
    queryFn: fetchFriends,
    staleTime: 30_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['friends'] });

  const friends = friendsQ.data;
  const incomingCount = friends?.incoming.length ?? 0;
  const totalRequests = incomingCount + (friends?.outgoing.length ?? 0);

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}><Users size={20} /></div>
        <div>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>
      </header>

      <div className={styles.tabs} role="tablist">
        <button
          role="tab"
          className={`${styles.tab} ${tab === 'find' ? styles.tabActive : ''}`}
          onClick={() => setTab('find')}
          aria-selected={tab === 'find'}
        >
          {t('tabs.find')}
        </button>
        <button
          role="tab"
          className={`${styles.tab} ${tab === 'friends' ? styles.tabActive : ''}`}
          onClick={() => setTab('friends')}
          aria-selected={tab === 'friends'}
        >
          {t('tabs.friends')}
          {friends && friends.accepted.length > 0 && (
            <span className={styles.tabBadge}>{friends.accepted.length}</span>
          )}
        </button>
        <button
          role="tab"
          className={`${styles.tab} ${tab === 'requests' ? styles.tabActive : ''}`}
          onClick={() => setTab('requests')}
          aria-selected={tab === 'requests'}
        >
          {t('tabs.requests')}
          {totalRequests > 0 && (
            <span className={`${styles.tabBadge} ${incomingCount > 0 ? styles.tabBadgeAlert : ''}`}>
              {totalRequests}
            </span>
          )}
        </button>
        <button
          role="tab"
          className={`${styles.tab} ${tab === 'activity' ? styles.tabActive : ''}`}
          onClick={() => setTab('activity')}
          aria-selected={tab === 'activity'}
        >
          {t('tabs.activity')}
        </button>
        <button
          role="tab"
          className={`${styles.tab} ${tab === 'leaderboard' ? styles.tabActive : ''}`}
          onClick={() => setTab('leaderboard')}
          aria-selected={tab === 'leaderboard'}
        >
          {t('tabs.leaderboard')}
        </button>
      </div>

      {friendsQ.isLoading && !friends && (
        <p className={styles.loading}>{t('loading')}</p>
      )}

      {friends && tab === 'friends' && (
        <FriendsTab cards={friends.accepted} onRefresh={refresh} onNavigate={onNavigate} />
      )}
      {friends && tab === 'requests' && (
        <RequestsTab
          incoming={friends.incoming}
          outgoing={friends.outgoing}
          onRefresh={refresh}
        />
      )}
      {tab === 'find' && <FindTab />}
      {tab === 'activity' && <ActivityFeedTab />}
      {tab === 'leaderboard' && <FriendsLeaderboardTabs />}
    </div>
  );
}

// ── Activity feed ──────────────────────────────────────────────────

function ActivityFeedTab() {
  const { t } = useTranslation('friends');
  const q = useInfiniteQuery({
    queryKey: ['feed'],
    queryFn: ({ pageParam }) => fetchFeed({
      limit: 25,
      ...(pageParam ? { before: pageParam } : {}),
    }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.hasMore ? last.nextBefore : undefined,
    staleTime: 30_000,
  });

  if (q.isLoading && !q.data) {
    return <p className={styles.loading}>{t('loading')}</p>;
  }
  const events = q.data?.pages.flatMap((p) => p.events) ?? [];
  if (events.length === 0) {
    return (
      <div className={styles.empty}>
        <NamedIcon
          name="activity"
          fallback={<Activity size={32} className={styles.emptyIcon} />}
          size={32}
        />
        <p className={styles.emptyTitle}>{t('activity.emptyTitle')}</p>
        <p className={styles.emptyBody}>{t('activity.emptyBody')}</p>
      </div>
    );
  }
  return (
    <div className={styles.list}>
      {events.map((e) => (
        <FeedRow key={e.id} event={e} />
      ))}
      {q.hasNextPage && (
        <button
          type="button"
          className={styles.loadMore}
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
        >
          {q.isFetchingNextPage
            ? <><Loader2 size={12} className={styles.searchSpin} /> {t('loading')}</>
            : t('activity.loadOlder')}
        </button>
      )}
    </div>
  );
}

function FeedRow({ event }: { event: FeedEventCard }) {
  const { t } = useTranslation('friends');
  const { icon: Icon, tone, line, badge } = describeFeedEvent(event, t);
  const initials = (event.author?.displayName ?? '?').slice(0, 1).toUpperCase();
  const who = event.isSelf ? t('feed.you') : (event.author?.displayName ?? t('feed.driverFallback'));
  return (
    <div className={`${styles.feedRow} ${styles[`feedTone_${tone}`] ?? ''}`}>
      <div className={styles.feedAvatar}>
        <Icon size={14} />
        <span className={styles.feedAvatarInitial}>{initials}</span>
      </div>
      <div className={styles.feedText}>
        <p className={styles.feedLine}>
          <strong>{who}</strong> {line}
        </p>
        <p className={styles.feedMeta}>
          {badge && <span className={styles.feedBadge}>{badge}</span>}
          <span className={styles.feedTime}>{formatRelativeTime(event.createdAt, t)}</span>
        </p>
      </div>
    </div>
  );
}

function describeFeedEvent(event: FeedEventCard, t: FriendsT): {
  icon: typeof Activity;
  tone: 'good' | 'warm' | 'charging' | 'warn' | 'neutral';
  line: string;
  badge?: string;
} {
  const p = event.payload ?? {};
  switch (event.type) {
    case 'BADGE_EARNED': {
      const name = String(p['badgeName'] ?? t('feed.fallbackBadge'));
      const pts = Number(p['pointsReward'] ?? 0);
      return {
        icon: Award, tone: 'good',
        line: t('feed.badgeEarned', { name }),
        badge: pts > 0 ? t('feed.pointsBadge', { pts }) : undefined,
      };
    }
    case 'STREAK_MILESTONE': {
      const name = String(p['streakName'] ?? t('feed.fallbackStreak'));
      const week = Number(p['weekCount'] ?? 0);
      const pts = Number(p['pointsEarned'] ?? 0);
      const is4 = Boolean(p['isBonus4Week']);
      return {
        icon: Flame, tone: is4 ? 'charging' : 'good',
        line: t('feed.streakMilestone', { week, name }),
        badge: is4 ? t('feed.pointsBonus4Week', { pts }) : t('feed.pointsBadge', { pts }),
      };
    }
    case 'CHALLENGE_COMPLETED': {
      const name = String(p['challengeName'] ?? t('feed.fallbackChallenge'));
      const pts = Number(p['pointsEarned'] ?? 0);
      return {
        icon: Target, tone: 'charging',
        line: t('feed.challengeCompleted', { name }),
        badge: t('feed.pointsBadge', { pts }),
      };
    }
    case 'RANK_UP': {
      const to = String(p['toRank'] ?? t('feed.fallbackRank'));
      return {
        icon: Medal, tone: 'charging',
        line: t('feed.rankUp', { rank: to }),
        badge: to.toUpperCase(),
      };
    }
    case 'FRIEND_ACCEPTED': {
      const name = String(p['friendDisplayName'] ?? t('feed.fallbackDriver'));
      return {
        icon: Users, tone: 'neutral',
        line: t('feed.friendAccepted', { name }),
      };
    }
    case 'UNLOCK_PURCHASED': {
      const name = String(p['unlockName'] ?? t('feed.fallbackUnlock'));
      const pts = Number(p['pointsSpent'] ?? 0);
      return {
        icon: Sparkles, tone: 'good',
        line: t('feed.unlockPurchased', { name }),
        badge: t('feed.pointsSpent', { pts }),
      };
    }
    case 'STREAK_AT_RISK': {
      const name = String(p['streakName'] ?? t('feed.fallbackStreak'));
      const cost = Number(p['freezeCost'] ?? 0);
      return {
        icon: Snowflake, tone: 'warn',
        line: t('feed.streakAtRisk', { name }),
        badge: t('feed.freezeBadge', { pts: cost }),
      };
    }
    default:
      return { icon: Activity, tone: 'neutral', line: t('feed.noteworthy') };
  }
}

function formatRelativeTime(iso: string, t: FriendsT): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return t('time.justNow');
  const m = Math.floor(diff / 60_000);
  if (m < 60) return t('time.minutes', { m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('time.hours', { h });
  const d = Math.floor(h / 24);
  if (d < 30) return t('time.days', { d });
  return new Date(iso).toLocaleDateString();
}

// ── Tabs ────────────────────────────────────────────────────────────

function FriendsTab({
  cards, onRefresh, onNavigate,
}: {
  cards: FriendCard[];
  onRefresh: () => void;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('friends');
  if (cards.length === 0) {
    return (
      <div className={styles.empty}>
        <Users size={32} className={styles.emptyIcon} />
        <p className={styles.emptyTitle}>{t('friendsEmpty.title')}</p>
        <p className={styles.emptyBody}>
          {t('friendsEmpty.bodyPre')}<strong>{t('tabs.find')}</strong>{t('friendsEmpty.bodyPost')}
        </p>
      </div>
    );
  }
  return (
    <div className={styles.list}>
      {cards.map((c) => (
        <FriendRow
          key={c.friendshipId}
          card={c}
          onAction={onRefresh}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

function RequestsTab({
  incoming, outgoing, onRefresh,
}: {
  incoming: FriendCard[]; outgoing: FriendCard[]; onRefresh: () => void;
}) {
  const { t } = useTranslation('friends');
  if (incoming.length === 0 && outgoing.length === 0) {
    return (
      <div className={styles.empty}>
        <UserPlus size={32} className={styles.emptyIcon} />
        <p className={styles.emptyTitle}>{t('requests.emptyTitle')}</p>
        <p className={styles.emptyBody}>{t('requests.emptyBody')}</p>
      </div>
    );
  }
  return (
    <div className={styles.list}>
      {incoming.length > 0 && (
        <>
          <h2 className={styles.sectionLabel}>{t('requests.incoming')}</h2>
          {incoming.map((c) => (
            <FriendRow key={c.friendshipId} card={c} onAction={onRefresh} />
          ))}
        </>
      )}
      {outgoing.length > 0 && (
        <>
          <h2 className={styles.sectionLabel}>{t('requests.sent')}</h2>
          {outgoing.map((c) => (
            <FriendRow key={c.friendshipId} card={c} onAction={onRefresh} />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * Share-invite card. Sits above the Find-tab search input.
 * Surfaces the user's referral_code as a shareable link via
 * navigator.share (native share sheet on iOS/Android PWAs)
 * with copy-to-clipboard fallback for desktop and for the
 * cancel-during-share case.
 *
 * Bonus copy ("You've invited X friends · earned Y points") is
 * driven by the existing /gamification/referral endpoint —
 * totalReferrals × referrerBonus. When the user has zero
 * referrals, the line is suppressed (zero-state copy would
 * read as a passive-aggressive nudge).
 */
function ShareInviteCard() {
  const { t } = useTranslation('friends');
  const [copying, setCopying] = useState(false);
  const referralQ = useQuery({
    queryKey: ['gamification', 'referral'],
    queryFn: fetchReferral,
    staleTime: 60_000,
    retry: false,
  });

  const code = referralQ.data?.code;
  // /register is the signup route. Links shared before 23 October 2026 say /auth/register, which App routes to
  // the same page; the code itself survives any sign-in from there (lib/pendingReferral.ts).
  const inviteUrl = code
    ? `${window.location.origin}/register?ref=${encodeURIComponent(code)}`
    : null;
  const totalReferrals = referralQ.data?.totalReferrals ?? 0;
  const referrerBonus = referralQ.data?.referrerBonus ?? 0;
  const refereeBonus = referralQ.data?.refereeBonus ?? 0;

  const handleShare = async () => {
    if (!inviteUrl) return;
    setCopying(true);
    const text = t('share.inviteText');

    // navigator.share path. Available on most modern mobile
    // browsers + iOS / Android PWAs. Wrapped in try/catch
    // because the user can cancel the share sheet, which
    // throws an AbortError; we treat cancel as "fall through
    // to copy" so the user always gets some way out.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: inviteUrl, text, title: t('share.sheetTitle') });
        setCopying(false);
        return;
      } catch {
        // Fall through to clipboard. Common cases: user hit
        // cancel; share-sheet not actually available despite
        // the function-typeof check (some Android WebViews
        // expose the symbol but reject all calls).
      }
    }

    // Clipboard fallback. navigator.clipboard.writeText is
    // available on the same modern browsers + secure contexts
    // (PWAs always serve from https or localhost so we're
    // good). Failures here surface as an alert so the user
    // can manually copy.
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast(t('share.toastCopied'));
    } catch {
      // Intentional alert() — NOT a toast migration candidate. This
      // is the manual-copy fallback when clipboard.writeText fails
      // (rare, mostly older Android WebViews). The user needs to
      // read and copy a 60+ char URL by hand; the toast pill auto-
      // dismisses at 1800ms, far too short for that. alert() blocks
      // until dismissed, which is exactly what's wanted here.
      window.alert(t('share.alertCopy', { url: inviteUrl }));
    } finally {
      setCopying(false);
    }
  };

  // Render a quiet placeholder while the code lazy-creates on
  // the backend. The first /gamification/referral call may
  // take a beat to allocate the code; the button is disabled
  // rather than shown in a half-broken state.
  if (referralQ.isLoading || !inviteUrl) {
    return (
      <div className={styles.shareCard}>
        <div className={styles.shareTitle}>{t('share.title')}</div>
        <p className={styles.shareDesc}>{t('share.loading')}</p>
      </div>
    );
  }

  return (
    <div className={styles.shareCard}>
      <div className={styles.shareTitle}>{t('share.title')}</div>
      {/*
       * Editorial: "and" not period. Both bonuses fire when the
       * invitee completes their first session — splitting with a
       * period would scope the gating clause to only the second
       * bonus. The "and" connector preserves the original meaning
       * (parallel bonuses, single trigger event) while removing
       * the comma splice in the original copy.
       */}
      <p className={styles.shareDesc}>
        {t('share.desc', { referee: refereeBonus, referrer: referrerBonus })}
      </p>
      <div className={styles.shareCodeRow}>
        <span className={styles.shareCodeLabel}>{t('share.codeLabel')}</span>
        <code className={styles.shareCode}>{code}</code>
      </div>
      <button
        type="button"
        className={styles.shareBtn}
        onClick={() => void handleShare()}
        disabled={copying}
      >
        {typeof navigator.share === 'function'
          ? <><Share2 size={14} /> {t('share.btnShare')}</>
          : <><Copy size={14} /> {t('share.btnCopy')}</>}
      </button>
      {totalReferrals > 0 && (
        <p className={styles.shareStat}>
          {t('share.statInvited', { count: totalReferrals })}
          {referrerBonus > 0 && ` ${t('share.statEarned', { pts: totalReferrals * referrerBonus })}`}
        </p>
      )}
    </div>
  );
}

/**
 * Add a friend by EXACT email — the ONLY way to add a friend (the people-
 * suggesting username / name / referral-code search was removed). The driver
 * types the friend's precise email and the server matches it exactly to a user
 * in the same tenant, creating a PENDING request that completes only when the
 * friend accepts.
 *
 * Privacy / enumeration resistance:
 *   - autoComplete="off" on the form AND the input (plus a non-email `name` and
 *     autocorrect/capitalize/spellcheck off) so the browser never offers saved
 *     or other people's email addresses.
 *   - A plain submit form, NOT a typeahead — nothing is suggested as you type.
 *   - The server response is IDENTICAL whether or not the email is registered,
 *     so this shows the SAME neutral message in every case: it never reveals
 *     whether the email belongs to an EVI driver.
 */
/** Looks like an email address (has an @ with something either side plus a
 *  dot in the domain). Anything else is treated as a friend code. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Plausible friend/referral code after uppercasing: the codes shown on the
 *  invite card are 8 alphanumerics (e.g. FPJREVS7); accept a tolerant range
 *  so format tweaks don't strand the field. */
const CODE_SHAPE = /^[A-Z0-9]{4,16}$/;

function AddByEmailOrCode() {
  const { t } = useTranslation('friends');
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const raw = value.trim();
    if (!raw || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      if (EMAIL_SHAPE.test(raw)) {
        await requestFriend({ recipientEmail: raw });
        // Enumeration-resistant: the server returns the SAME neutral response
        // whether or not the email matched, so the message is identical in both
        // cases -- it never confirms whether the email is registered. Do NOT
        // refetch here: a match-only friends-list change would itself leak.
        setMsg({
          ok: true,
          text: t('add.msgEmailSent'),
        });
        setValue('');
      } else {
        // Friend-code path. Codes are shareable identifiers (the invite card
        // hands them out), so unlike email the server keeps SPECIFIC responses:
        // not found, can't friend yourself, already pending, cooldown. Exact
        // match only -- this is not a search and suggests nothing.
        const code = raw.toUpperCase();
        if (!CODE_SHAPE.test(code)) {
          setMsg({ ok: false, text: t('add.msgInvalid') });
          return;
        }
        await requestFriend({ recipientReferralCode: code });
        setMsg({ ok: true, text: t('add.msgRequestSent') });
        setValue('');
        // Specific responses by design on the code path, so refreshing the
        // friends list (outgoing row appears) leaks nothing.
        await qc.invalidateQueries({ queryKey: ['friends'] });
      }
    } catch (err) {
      // Code-path API errors carry the server's own message (Driver not
      // found / You can't friend yourself / Wait before re-requesting...).
      // Email-path errors only ever mean transport/5xx, never existence.
      let text = t('add.msgError');
      if (err instanceof HTTPError && err.response.status < 500) {
        try {
          const body = await err.response.json() as { error?: { message?: string } };
          if (body.error?.message) text = body.error.message;
        } catch { /* keep the generic text */ }
      }
      setMsg({ ok: false, text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.emailCard} onSubmit={submit} autoComplete="off">
      <div className={styles.emailTitle}>{t('add.title')}</div>
      <p className={styles.emailHint}>{t('add.hint')}</p>
      <div className={styles.emailRow}>
        <input
          type="text"
          name="add-friend-email-or-code"
          className={styles.emailInput}
          placeholder={t('add.placeholder')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          disabled={busy}
          aria-label={t('add.ariaLabel')}
        />
        <button
          type="submit"
          className={styles.emailBtn}
          disabled={busy || !value.trim()}
        >
          {busy
            ? <Loader2 size={14} className={styles.emailSpin} />
            : <><UserPlus size={12} /> {t('add.button')}</>}
        </button>
      </div>
      {msg && <p className={msg.ok ? styles.emailOk : styles.errorMsg}>{msg.text}</p>}
    </form>
  );
}

function FindTab() {
  return (
    <div className={styles.findBlock}>
      {/* Share your OWN invite link (no other users are surfaced). The exact-
          match field below is the ONLY way to add a friend: a precise email
          (enumeration-neutral) or an exact friend code (shareable by design).
          No typeahead, no search -- nothing is ever suggested. */}
      <ShareInviteCard />
      <AddByEmailOrCode />
    </div>
  );
}

// ── Friend row primitive ────────────────────────────────────────────

function FriendRow({
  card, onAction, onNavigate,
}: {
  card: FriendCard;
  onAction: () => void;
  /** Optional — when present AND the row is for an accepted
   *  friend, the row body becomes tap-through to /friends/:userId.
   *  Pending rows stay non-navigating because the detail page
   *  404s on non-accepted friendships. */
  onNavigate?: (path: string) => void;
}) {
  const { t } = useTranslation('friends');
  const [pending, setPending] = useState(false);

  const onAccept = async () => {
    setPending(true);
    try { await acceptFriend(card.friendshipId); onAction(); } finally { setPending(false); }
  };
  const onDecline = async () => {
    setPending(true);
    try { await declineFriend(card.friendshipId); onAction(); } finally { setPending(false); }
  };
  const onRemove = async () => {
    if (!window.confirm(t('row.confirmRemove'))) return;
    setPending(true);
    try { await removeFriend(card.friendshipId); onAction(); } finally { setPending(false); }
  };

  const initials = (card.user.username ?? card.user.name ?? '?').slice(0, 1).toUpperCase();
  const tappable = card.status === 'accepted' && Boolean(onNavigate);
  const goToDetail = () => {
    if (tappable && onNavigate) onNavigate(`/friends/${card.user.id}`);
  };

  return (
    <div className={styles.friendRow}>
      {/* Body is tap-through to the detail page when accepted.
          Implemented as a transparent <button> wrapping the body
          rather than a click handler on the row div, so screen
          readers + keyboard nav land on a real activator. The
          action buttons sit OUTSIDE this body button so taps on
          them don't fire detail navigation. */}
      <button
        type="button"
        className={tappable ? styles.friendRowBodyTappable : styles.friendRowBody}
        onClick={goToDetail}
        disabled={!tappable}
        aria-label={tappable ? t('row.viewDetail', { name: card.user.username ?? card.user.name ?? t('row.friendFallback') }) : undefined}
      >
        <div className={styles.avatar}>{initials}</div>
        <div className={styles.friendText}>
          <p className={styles.rowName}>{card.user.username ?? card.user.name ?? t('row.nameFallback')}</p>
          <p className={styles.rowStats}>
            <Trophy size={11} /> {card.stats.currentRank ?? 'Silver'}
            <span className={styles.dot}>·</span>
            <Zap size={11} /> {card.stats.totalKwh.toFixed(1)} kWh
            {card.stats.streakDays > 0 && (
              <>
                <span className={styles.dot}>·</span>
                <Flame size={11} /> {card.stats.streakDays}d
              </>
            )}
            {/* Weekly charging streak — CalendarRange, not Flame
                (the flame is the daily streak). Same hide-at-0
                convention as the flame on this surface. */}
            {card.stats.streakWeeks > 0 && (
              <>
                <span className={styles.dot}>·</span>
                <CalendarRange size={11} /> {card.stats.streakWeeks}w
              </>
            )}
          </p>
        </div>
      </button>
      <div className={styles.actions}>
        {card.status === 'pending' && card.direction === 'incoming' && (
          <>
            <button className={styles.iconBtn} onClick={onAccept} disabled={pending} aria-label={t('row.accept')}>
              <Check size={14} />
            </button>
            <button className={styles.iconBtnGhost} onClick={onDecline} disabled={pending} aria-label={t('row.decline')}>
              <X size={14} />
            </button>
          </>
        )}
        {card.status === 'pending' && card.direction === 'outgoing' && (
          <span className={styles.tag}>{t('requests.sent')}</span>
        )}
        {card.status === 'accepted' && (
          <button className={styles.iconBtnGhost} onClick={onRemove} disabled={pending} aria-label={t('row.remove')} title={t('row.removeTitle')}>
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
